import {
  Box,
  Button,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import { useState } from "react";
import Icfes1 from "../../Images/icfes-1.png";
import Icfes2 from "../../Images/icfes-2.png";
import Icfes3 from "../../Images/icfes-3.png";
import Icfes4 from "../../Images/icfes-4.png";
import { EXTERNAL_LINKS } from "../../Config/constants";

const steps = [
  {
    label: "Accede al portal ICFES",
    description: (
      <>
        Ingresa al portal de{" "}
        <Link
          href={EXTERNAL_LINKS.ICFES_RESULTS}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ fontWeight: 600 }}
        >
          consulta de resultados ICFES
          <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }} />
        </Link>
        . Diligencia tu número de documento o número de registro, selecciona el
        año de presentación y periodo, completa el captcha y haz clic en
        "Entrar".
      </>
    ),
    image: Icfes1,
    imageAlt: "Pantalla de consulta de ICFES",
  },
  {
    label: "Busca tu registro",
    description:
      "Te mostrará la pantalla con el número de registro y el botón para ver el detalle de tu prueba. Haz clic en el botón de ver detalle.",
    image: Icfes2,
    imageAlt: "Pantalla con número de registro",
  },
  {
    label: "Solicita ver resultados",
    description:
      'Aparecerá un cuadro de diálogo con opciones. Haz clic en "Ver resultados" para continuar.',
    image: Icfes3,
    imageAlt: "Cuadro de diálogo de opciones",
  },
  {
    label: "Obtén tu puntaje recalificado",
    description:
      "Te redireccionará a la pantalla con la recalificación. Debes tomar el puntaje global que aparece marcado en rojo.",
    image: Icfes4,
    imageAlt: "Pantalla con puntaje recalificado",
    highlight: true,
  },
];

const IcfesDialog = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleClose = () => {
    onClose();
    // Reset to first step after closing
    setTimeout(() => setActiveStep(0), 300);
  };

  return (
    <Dialog
      onClose={handleClose}
      open={open}
      scroll="paper"
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? "100%" : "90vh",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: "secondary.main",
          color: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoIcon />
          <Typography variant="h6" component="span" fontWeight={600}>
            Cómo obtener tu puntaje recalificado
          </Typography>
        </Box>
        <IconButton
          aria-label="cerrar"
          onClick={handleClose}
          sx={{ color: "primary.main" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Subtitle */}
      <Box sx={{ px: 3, py: 2, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
        <Typography variant="body2" color="text.secondary">
          Para pruebas presentadas entre el <strong>primer periodo de 2012</strong> y el{" "}
          <strong>primer periodo de 2014</strong>, debes obtener tu puntaje
          recalificado directamente del portal ICFES.
        </Typography>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  optional={
                    step.highlight && (
                      <Chip
                        label="Importante"
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                      />
                    )
                  }
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontWeight: activeStep === index ? 600 : 400,
                      fontSize: "1rem",
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
                <StepContent>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {step.description}
                  </Typography>

                  {/* Image Preview */}
                  <Paper
                    elevation={2}
                    sx={{
                      overflow: "hidden",
                      borderRadius: 2,
                      mb: 2,
                      border: step.highlight ? "2px solid" : "1px solid",
                      borderColor: step.highlight ? "error.main" : "divider",
                    }}
                  >
                    <CardMedia
                      component="img"
                      image={step.image}
                      alt={step.imageAlt}
                      sx={{
                        objectFit: "contain",
                        maxHeight: 300,
                        width: "100%",
                        bgcolor: "grey.100",
                      }}
                    />
                  </Paper>

                  {/* Step Navigation */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      sx={{ mt: 1 }}
                    >
                      {index === steps.length - 1 ? "Finalizar" : "Continuar"}
                    </Button>
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1 }}
                    >
                      Atrás
                    </Button>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>

          {/* Completion State */}
          {activeStep === steps.length && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                bgcolor: "success.light",
                borderRadius: 2,
                textAlign: "center",
              }}
            >
              <Typography variant="h6" color="success.dark" gutterBottom>
                ¡Listo!
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                Ya conoces cómo obtener tu puntaje recalificado. Ahora puedes
                ingresar ese valor en el simulador.
              </Typography>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                <Button onClick={handleReset} variant="outlined">
                  Ver pasos de nuevo
                </Button>
                <Button onClick={handleClose} variant="contained">
                  Comenzar a simular
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
        <Button
          href={EXTERNAL_LINKS.ICFES_RESULTS}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<OpenInNewIcon />}
          variant="outlined"
        >
          Ir al portal ICFES
        </Button>
        <Button onClick={handleClose} variant="text">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

IcfesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default IcfesDialog;
